import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, unwrap } from '../../api/client';
import { errorMessage } from '../../api/errors';
import type { components } from '../../api/schema';
import { useAuth } from '../../auth/AuthProvider';
import {
  Badge,
  Button,
  Card,
  Drawer,
  Field,
  Input,
  PageHeader,
  Pagination,
  Select,
  Table,
} from '../../design';
import { useToast } from '../../design/Toast';
import { fmtDate } from '../../lib/format';
import { queryParams, useUrlState } from '../../lib/urlState';
import { useForm } from 'react-hook-form';

type Org = components['schemas']['Organization'];
type OrgCreate = components['schemas']['OrganizationCreate'];

export function OrgsPage() {
  const { user, withStepUp } = useAuth();
  const toast = useToast();
  const qc = useQueryClient();
  const { values, page, pageSize, set } = useUrlState(['q'] as const);
  const [editing, setEditing] = useState<Org | 'new' | null>(null);

  const query = useQuery({
    queryKey: ['orgs', { q: values.q, page, pageSize }],
    queryFn: () =>
      unwrap(api.GET('/orgs', { params: { query: queryParams({ q: values.q, page, pageSize }) } })),
  });

  const remove = useMutation({
    mutationFn: (org: Org) =>
      withStepUp((headers) =>
        unwrap(api.DELETE('/orgs/{id}', { params: { path: { id: org.id }, header: headers } })),
      ),
    onSuccess: () => {
      toast('Organizația a fost ștearsă.', 'success');
      setEditing(null);
      void qc.invalidateQueries({ queryKey: ['orgs'] });
    },
    onError: (e) => toast(errorMessage(e), 'error'),
  });

  const global = user?.orgId === null;

  return (
    <>
      <PageHeader
        title="Organizații & locații"
        subtitle="Tenant-urile platformei: clienți cu lockere, utilizatori și perimetru de date propriu."
        actions={
          global ? (
            <Button variant="primary" icon="plus" onClick={() => setEditing('new')}>
              Organizație nouă
            </Button>
          ) : undefined
        }
      />
      <Card tight>
        <div className="flex" style={{ padding: 16 }}>
          <Input
            placeholder="Caută după nume sau cod…"
            value={values.q}
            onChange={(e) => set({ q: e.target.value })}
            style={{ maxWidth: 320 }}
            aria-label="Căutare"
          />
          {query.isFetching && <span className="muted small">se actualizează…</span>}
        </div>
        <Table<Org>
          columns={[
            { key: 'name', header: 'Organizație', render: (o) => <strong>{o.name}</strong> },
            { key: 'code', header: 'Cod', render: (o) => <span className="mono">{o.code}</span> },
            { key: 'city', header: 'Oraș', render: (o) => o.city },
            {
              key: 'sla',
              header: 'SLA',
              render: (o) => (
                <Badge tone={o.sla === '24x7' ? 'purple' : 'blue'}>
                  {o.sla === '24x7' ? 'Non-stop' : 'Program de lucru'}
                </Badge>
              ),
            },
            {
              key: 'contact',
              header: 'Contact',
              render: (o) => <a href={`mailto:${o.contactEmail}`}>{o.contactEmail}</a>,
            },
            {
              key: 'created',
              header: 'Creată',
              render: (o) => <span className="muted">{fmtDate(o.createdAt)}</span>,
            },
          ]}
          rows={query.data?.items ?? []}
          rowKey={(o) => o.id}
          onRowClick={(o) => setEditing(o)}
          selectedKey={editing && editing !== 'new' ? editing.id : null}
          empty={
            query.isLoading
              ? 'Se încarcă…'
              : query.error
                ? errorMessage(query.error)
                : 'Nicio organizație.'
          }
          footer={
            query.data && (
              <Pagination
                page={query.data.page}
                pageSize={query.data.pageSize}
                total={query.data.total}
                onPage={(p) => set({ page: p })}
              />
            )
          }
        />
      </Card>
      {editing && (
        <OrgDrawer
          org={editing === 'new' ? null : editing}
          readOnly={!global}
          onClose={() => setEditing(null)}
          onDelete={editing !== 'new' && global ? () => remove.mutate(editing) : undefined}
          deleting={remove.isPending}
        />
      )}
    </>
  );
}

function OrgDrawer({
  org,
  readOnly,
  onClose,
  onDelete,
  deleting,
}: {
  org: Org | null;
  readOnly: boolean;
  onClose: () => void;
  onDelete?: () => void;
  deleting: boolean;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const form = useForm<OrgCreate>({
    defaultValues: org
      ? {
          name: org.name,
          code: org.code,
          city: org.city,
          sla: org.sla,
          contactEmail: org.contactEmail,
          documentRetentionDays: org.documentRetentionDays,
        }
      : {
          name: '',
          code: '',
          city: '',
          sla: 'business',
          contactEmail: '',
          documentRetentionDays: 90,
        },
  });
  const save = useMutation({
    mutationFn: async (data: OrgCreate) => {
      const body = { ...data, documentRetentionDays: Number(data.documentRetentionDays) };
      if (org) {
        const { code: _code, ...patch } = body;
        return unwrap(api.PATCH('/orgs/{id}', { params: { path: { id: org.id } }, body: patch }));
      }
      return unwrap(api.POST('/orgs', { body }));
    },
    onSuccess: () => {
      toast(org ? 'Organizația a fost actualizată.' : 'Organizația a fost creată.', 'success');
      void qc.invalidateQueries({ queryKey: ['orgs'] });
      onClose();
    },
    onError: (e) => toast(errorMessage(e), 'error'),
  });
  const errors = form.formState.errors;
  return (
    <Drawer
      title={org ? org.name : 'Organizație nouă'}
      onClose={onClose}
      footer={
        readOnly ? (
          <Button onClick={onClose}>Închide</Button>
        ) : (
          <>
            {onDelete &&
              (confirmDelete ? (
                <Button variant="danger" onClick={onDelete} busy={deleting}>
                  Confirmă ștergerea
                </Button>
              ) : (
                <Button variant="danger" icon="trash" onClick={() => setConfirmDelete(true)}>
                  Șterge
                </Button>
              ))}
            <span className="grow" />
            <Button onClick={onClose}>Renunță</Button>
            <Button
              variant="primary"
              onClick={form.handleSubmit((d) => save.mutate(d))}
              busy={save.isPending}
            >
              Salvează
            </Button>
          </>
        )
      }
    >
      <form className="stack" onSubmit={form.handleSubmit((d) => save.mutate(d))}>
        <Field label="Nume" error={errors.name?.message} htmlFor="org-name">
          <Input
            id="org-name"
            disabled={readOnly}
            invalid={!!errors.name}
            {...form.register('name', {
              required: 'Numele este obligatoriu',
              minLength: { value: 2, message: 'Minimum 2 caractere' },
            })}
          />
        </Field>
        <Field
          label="Cod"
          hint="2–8 caractere, litere mari și cifre; nu se mai schimbă după creare"
          error={errors.code?.message}
          htmlFor="org-code"
        >
          <Input
            id="org-code"
            disabled={readOnly || !!org}
            invalid={!!errors.code}
            {...form.register('code', {
              required: 'Codul este obligatoriu',
              pattern: {
                value: /^[A-Z0-9]{2,8}$/,
                message: 'Doar litere mari și cifre, 2–8 caractere',
              },
              setValueAs: (v: string) => v.toUpperCase().trim(),
            })}
          />
        </Field>
        <Field label="Oraș" error={errors.city?.message} htmlFor="org-city">
          <Input
            id="org-city"
            disabled={readOnly}
            invalid={!!errors.city}
            {...form.register('city', { required: 'Orașul este obligatoriu' })}
          />
        </Field>
        <Field label="SLA" htmlFor="org-sla">
          <Select id="org-sla" disabled={readOnly} {...form.register('sla')}>
            <option value="business">Program de lucru</option>
            <option value="24x7">Non-stop (24x7)</option>
          </Select>
        </Field>
        <Field label="E-mail de contact" error={errors.contactEmail?.message} htmlFor="org-email">
          <Input
            id="org-email"
            type="email"
            disabled={readOnly}
            invalid={!!errors.contactEmail}
            {...form.register('contactEmail', { required: 'E-mailul este obligatoriu' })}
          />
        </Field>
        <Field
          label="Retenție documente (zile)"
          hint="Implicitul documentelor încărcate fără termen propriu; 1–3650"
          error={errors.documentRetentionDays?.message}
          htmlFor="org-retention"
        >
          <Input
            id="org-retention"
            type="number"
            min={1}
            max={3650}
            disabled={readOnly}
            invalid={!!errors.documentRetentionDays}
            {...form.register('documentRetentionDays', {
              required: 'Retenția este obligatorie',
              min: { value: 1, message: 'Minimum 1' },
              max: { value: 3650, message: 'Maximum 3650' },
            })}
          />
        </Field>
        {confirmDelete && (
          <p className="small" style={{ color: 'var(--red)' }}>
            Ștergerea este refuzată dacă organizația are lockere sau utilizatori. Cere confirmare
            suplimentară (cod TOTP).
          </p>
        )}
      </form>
    </Drawer>
  );
}
